import { useEffect, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { MaskLine } from "@/components/site/Reveal";
import { BankBox } from "@/components/checkout/BankBox";
import { useSeo } from "@/lib/seo";
import { SHOP } from "@/lib/shop";
import { SubAfterOrder } from "@/components/subscription/SubPromo";
import { track } from "@/lib/ads";
import { T, useLang, shopPath } from "@/lib/i18n";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const ref = params.get("ref");
  const isSub = params.get("abonnement") === "1";
  const { clear, items } = useCart();
  const [bought] = useState(() => items);
  const [state, setState] = useState("checking");
  const [reason, setReason] = useState("");
  const [resa, setResa] = useState(null);
  const [mail, setMail] = useState(null);
  const { t, lang } = useLang();
  useSeo({ title: t("Votre commande | Ozentya"), noindex: true });

  useEffect(() => {
    if (!sid && !ref) {
      setState("error");
      return undefined;
    }
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(ref ? `/orders/status/${ref}` : `/payments/status/${sid}`);
        if (data?.payment_method === "reservation" && data?.payment_status === "pending") {
          setResa(data);
          setState("reserved");
          clear();
          return;
        }
        if (data?.payment_status === "paid") {
          track(isSub ? "Subscribe" : "Purchase", { value: Number(data?.amount_total || data?.amount || 0) });
          setMail(data?.email_masked || null);
          setState("paid");
          clear();
          return;
        }
        if (["expired", "failed", "cancelled"].includes(data?.payment_status) || data?.status === "expired") {
          setReason(data?.payment_error || "");
          setState("error");
          return;
        }
      } catch {
        /* retry */
      }
      tries += 1;
      if (tries < 15) timer = setTimeout(poll, 2000);
      else setState("pending");
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sid, ref]);

  return (
    <main data-testid="payment-success-page" className="flex min-h-screen flex-col items-center justify-center px-5 pb-16 pt-36 text-center">
      {state === "checking" && <Loader2 data-testid="payment-checking" className="h-8 w-8 animate-spin text-[var(--gold)]" />}
      {state === "reserved" && (
        <div data-testid="payment-reserved">
          <p className="eyebrow mb-6"><T>Commande réservée</T></p>
          <h1 className="font-display text-4xl italic sm:text-5xl"><T>Merci, votre rituel vous est réservé.</T></h1>
          <p className="mx-auto mt-6 max-w-md text-sm text-[var(--parchment)]/85 md:text-base">{t(resa?.bank ? "Aucun montant n'a été débité. Pour confirmer votre commande, réglez-la par virement avec les coordonnées ci-dessous (également envoyées par email). Votre colis part dès réception du virement." : "Aucun montant n'a été débité. Vous recevez un email récapitulatif et notre équipe vous contacte rapidement pour finaliser le règlement en toute sécurité. Votre colis part dès réception du paiement.")}</p>
          {resa?.bank ? <BankBox bank={resa.bank} amount={resa.amount} reference={resa.reference} payBefore={resa.pay_before} /> : null}
          {ref ? <p data-testid="payment-reference" className="mt-4 text-xs uppercase tracking-[0.2em] text-[var(--gold-soft)]"><T>Commande n° </T>{ref}</p> : null}
          <Link to={shopPath(lang)} className="mx-auto mt-8 flex h-12 w-fit items-center rounded-full border border-[var(--line)] px-8 text-xs uppercase tracking-[0.24em]"><T>Continuer mes achats</T></Link>
        </div>
      )}
      {state === "paid" && isSub && (
        <div data-testid="payment-sub-paid">
          <p className="eyebrow mb-6">Abonnement activé</p>
          <h1 className="font-display text-5xl md:text-7xl"><MaskLine>Bienvenue,</MaskLine><MaskLine delay={0.15}><span className="italic gold-text">vos soins arrivent.</span></MaskLine></h1>
          <p className="mx-auto mt-8 max-w-md text-[var(--muted)]">Votre abonnement est actif et votre premier colis est en préparation. Vous recevez un email{mail ? <> à <b data-testid="payment-email-masked" className="text-[var(--parchment)]">{mail}</b></> : null} avec votre lien « Mon abonnement » pour changer vos soins, mettre en pause ou arrêter quand vous voulez.</p>
          <p className="mx-auto mt-3 max-w-md text-sm text-[var(--parchment)]/80">Vous ne le voyez pas ? Regardez vos courriers indésirables ou demandez un nouveau lien sur la page <Link to="/mon-abonnement" data-testid="payment-sub-manage" className="text-[var(--gold-soft)] underline underline-offset-4">Mon abonnement</Link>.</p>
        </div>
      )}
      {state === "paid" && !isSub && (
        <div data-testid="payment-paid">
          <p className="eyebrow mb-6"><T>Commande confirmée</T></p>
          <h1 className="font-display text-5xl md:text-7xl"><MaskLine><T>Merci,</T></MaskLine><MaskLine delay={0.15}><span className="italic gold-text"><T>votre rituel arrive.</T></span></MaskLine></h1>
          <p className="mx-auto mt-8 max-w-md text-[var(--muted)]"><T>Votre paiement est validé : inutile de commander à nouveau. Un email de confirmation vous est envoyé</T>{mail ? <>{t(" à ")}<b data-testid="payment-email-masked" className="text-[var(--parchment)]">{mail}</b></> : null}<T>. Nous préparons votre colis avec soin.</T></p>
          <p data-testid="payment-contact-hint" className="mx-auto mt-3 max-w-md text-sm text-[var(--parchment)]/80"><T>Ce n'est pas votre adresse ou rien reçu ? Appelez-nous au </T><a href={`tel:${SHOP.tel}`} data-testid="payment-phone" className="text-[var(--gold-soft)] underline underline-offset-4">{SHOP.phone}</a>.</p>
          <p data-testid="payment-spam-hint" className="mx-auto mt-3 max-w-md text-sm text-[var(--parchment)]/80"><T>Vous ne le voyez pas ? Pensez à regarder dans vos courriers indésirables, puis marquez-le « Pas indésirable » pour recevoir nos prochains emails (suivi de colis) directement.</T></p>
          {ref ? <p data-testid="payment-reference" className="mt-4 text-xs uppercase tracking-[0.2em] text-[var(--gold-soft)]"><T>Commande n° </T>{ref}</p> : null}
          {lang === "en" ? null : <SubAfterOrder items={bought} />}
        </div>
      )}
      {state === "pending" && <div data-testid="payment-pending"><p className="font-display text-3xl italic"><T>Paiement en cours de vérification…</T></p><p className="mx-auto mt-4 max-w-md text-sm text-[var(--muted)]"><T>Votre banque n'a pas encore confirmé. Vous recevrez un email dès validation — inutile de payer à nouveau.</T></p></div>}
      {state === "error" && <div data-testid="payment-error"><p className="font-display text-3xl italic"><T>Le paiement n'a pas abouti.</T></p><p className="mx-auto mt-4 max-w-md text-sm text-[var(--muted)]">{t(reason || "Aucun montant n'a été débité. Vous pouvez réessayer avec le même ou un autre moyen de paiement.")}</p><Link to="/commande" data-testid="payment-retry" className="mx-auto mt-8 flex h-12 w-fit items-center rounded-full bg-[var(--gold)] px-8 text-xs font-semibold uppercase tracking-[0.24em] text-[var(--ink)]"><T>Réessayer</T></Link></div>}
      <Link to={shopPath(lang)} data-testid="payment-back-shop" className="mt-12 text-xs uppercase tracking-[0.24em] text-[var(--gold-soft)] underline-offset-8 hover:underline"><T>Retour à la boutique</T></Link>
    </main>
  );
}

export function PaymentCancel() {
  const [params] = useSearchParams();
  const ref = params.get("ref");
  const [paid, setPaid] = useState(false);
  const { t, lang } = useLang();
  useSeo({ title: lang === "en" ? "Payment cancelled | Ozentya" : "Paiement annulé | Ozentya", noindex: true });
  useEffect(() => {
    if (!ref) return undefined;
    let alive = true;
    api.get(`/orders/status/${ref}`).then(({ data }) => {
      if (!alive) return;
      if (data?.payment_status === "paid") setPaid(true);
      else api.post(`/orders/${ref}/cancel`).catch(() => null);
    }).catch(() => null);
    return () => { alive = false; };
  }, [ref]);
  if (paid) return <Navigate to={`/payment/success?ref=${ref}`} replace />;
  return (
    <main data-testid="payment-cancel-page" className="flex min-h-screen flex-col items-center justify-center px-5 pb-16 pt-36 text-center">
      <p className="eyebrow mb-6">{t("Paiement annulé ou refusé")}</p>
      <h1 className="font-display text-5xl italic md:text-6xl">{t("Votre panier vous attend.")}</h1>
      <p className="mx-auto mt-6 max-w-md text-sm text-[var(--muted)]">{t("Aucun montant n'a été débité. Vous pouvez réessayer quand vous le souhaitez.")}</p>
      <div className="mt-12 flex flex-wrap justify-center gap-4">
        <Link to="/commande" data-testid="payment-cancel-retry" className="flex h-14 items-center rounded-full bg-[var(--gold)] px-8 text-xs font-semibold uppercase tracking-[0.24em] text-[var(--ink)]">{t("Réessayer le paiement")}</Link>
        <Link to={shopPath(lang)} data-testid="payment-cancel-back" className="flex h-14 items-center rounded-full border border-[var(--line)] px-8 text-xs font-semibold uppercase tracking-[0.24em]">{t("Continuer mes achats")}</Link>
      </div>
    </main>
  );
}
